import { EndpointUrl } from 'api/endpoint';
import axios from 'axios';

const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB
const CONCURRENT_UPLOADS = 5;

export type UploadFileResult = {
  fileName: string;
  uploadId: string;
  workspaceId: string;
};

export type UploadFileCallbacks = {
  onProgress?: (progress: number) => void;
  onStatus?: (status: string) => void;
};

export async function uploadFileToWorkspace(
  workspaceId: string,
  file: File,
  callbacks: UploadFileCallbacks = {},
): Promise<UploadFileResult> {
  const { onProgress, onStatus } = callbacks;
  const token = localStorage.getItem('token');
  const headers = { 'x-finsight-token': token || '' };

  onStatus?.('Creating upload...');
  const createResponse = await axios.post(
    `${EndpointUrl}/workspace/${workspaceId}/files/create`,
    { fileName: file.name, fileSize: file.size },
    { headers },
  );

  const { uploadId } = createResponse.data;

  if (file.size > CHUNK_SIZE) {
    onStatus?.('Starting multipart upload...');
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const partProgress = new Array(totalChunks).fill(0);

    for (let i = 0; i < totalChunks; i += CONCURRENT_UPLOADS) {
      const batch = [];
      for (let j = 0; j < CONCURRENT_UPLOADS && i + j < totalChunks; j++) {
        const partIndex = i + j;
        const start = partIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const partNumber = partIndex + 1;
        const chunk = file.slice(start, end);
        const formData = new FormData();
        formData.append('file', chunk);

        const uploadPromise = axios.post(
          `${EndpointUrl}/workspace/${workspaceId}/files/${uploadId}/upload?partNumber=${partNumber}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'x-finsight-token': token || '',
            },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                partProgress[partIndex] =
                  progressEvent.loaded / progressEvent.total;
                const total =
                  partProgress.reduce((s, p) => s + p, 0) / totalChunks;
                onProgress?.(Math.round(total * 100));
              }
            },
          },
        );
        batch.push(uploadPromise);
      }

      onStatus?.(
        `Uploading parts ${i + 1}-${Math.min(i + CONCURRENT_UPLOADS, totalChunks)}/${totalChunks}...`,
      );
      await Promise.all(batch);
      onProgress?.(Math.round(((i + batch.length) / totalChunks) * 100));
    }
    onProgress?.(100);
    onStatus?.('Upload completed!');
  } else {
    onStatus?.('Uploading file...');
    const formData = new FormData();
    formData.append('file', file);

    await axios.post(
      `${EndpointUrl}/workspace/${workspaceId}/files/${uploadId}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-finsight-token': token || '',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            onProgress?.(
              Math.round((progressEvent.loaded / progressEvent.total) * 100),
            );
          }
        },
      },
    );
    onProgress?.(100);
    onStatus?.('Upload completed!');
  }

  return createResponse.data;
}
